import { useCallback, useEffect, useRef, useState } from 'react'
import { Anchor, Button, Group, Stack, Table, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { AppModal } from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import { useOpenTicketFromList } from '@/lib/open-ticket-navigation'
import { formatDateTimeDot } from '@/lib/ticket-display'
import type { OperatorTicketListRow, TicketListItem } from '@/types/api'

const BLOCKS = new Set([
  'total_tickets',
  'resolved_tickets',
  'closed_tickets',
  'closed_with_review',
  'closed_without_review',
  'product_review_requests',
])

export type OperatorTicketsBlock =
  | 'total_tickets'
  | 'resolved_tickets'
  | 'closed_tickets'
  | 'closed_with_review'
  | 'closed_without_review'
  | 'product_review_requests'

export function OperatorTicketsModal({
  opened,
  onClose,
  operatorId,
  block,
  blockLabel,
  analyticsParams,
}: {
  opened: boolean
  onClose: () => void
  operatorId: string
  block: OperatorTicketsBlock
  blockLabel: string
  analyticsParams: Record<string, string | number | boolean>
}) {
  const openTicket = useOpenTicketFromList()
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<OperatorTicketListRow[]>([])
  const [total, setTotal] = useState(0)
  const [frozenAnalyticsParams, setFrozenAnalyticsParams] = useState<
    Record<string, string | number | boolean>
  >({})
  const modalScopeRef = useRef('')
  const pageSize = 20

  const load = useCallback(async () => {
    if (!opened || !operatorId || !BLOCKS.has(block)) return
    setLoading(true)
    try {
      const res = await api.operatorTicketsBlock({
        operator_id: operatorId,
        block,
        page,
        page_size: pageSize,
        ...frozenAnalyticsParams,
      })
      setRows(res.tickets ?? [])
      setTotal(res.total ?? 0)
    } catch (e) {
      showNotification({
        color: 'red',
        title: 'Список обращений',
        message: e instanceof ApiError ? e.message : 'Не удалось загрузить',
      })
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [opened, operatorId, block, page, frozenAnalyticsParams])

  useEffect(() => {
    if (!opened) {
      modalScopeRef.current = ''
      return
    }
    const scope = `${operatorId}:${block}`
    if (modalScopeRef.current === scope) return
    modalScopeRef.current = scope
    // Фиксируем фильтры в момент открытия/смены списка, чтобы модалка не
    // перезагружалась на каждый ввод в полях периода.
    setFrozenAnalyticsParams(analyticsParams)
    setPage(1)
  }, [opened, operatorId, block, analyticsParams])

  useEffect(() => {
    void load()
  }, [load])

  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const isProduct = block === 'product_review_requests'

  return (
    <AppModal opened={opened} onClose={onClose} title={blockLabel} size="xl">
      <Stack gap="md">
        <Table striped highlightOnHover layout="fixed">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Обращение</Table.Th>
              <Table.Th>Проект</Table.Th>
              {isProduct ? (
                <>
                  <Table.Th>Запрошен</Table.Th>
                  <Table.Th>Получен</Table.Th>
                </>
              ) : (
                <>
                  <Table.Th>Статус</Table.Th>
                  <Table.Th>Создан</Table.Th>
                </>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text size="sm" c="dimmed">
                    Загрузка…
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text size="sm" c="dimmed">
                    Нет записей
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              rows.map((t) => {
                const display = t.project_slug
                  ? `${t.project_slug}-${t.ticket_number}`
                  : `#${t.ticket_number}`
                return (
                  <Table.Tr key={t.id}>
                    <Table.Td>
                      <Anchor
                        component="button"
                        type="button"
                        size="sm"
                        onClick={() =>
                          openTicket({
                            id: t.id,
                            ticket_number: t.ticket_number,
                            project_slug: t.project_slug ?? null,
                            subject: t.subject ?? '',
                            status: t.status ?? '',
                            priority: 'normal',
                            customer_name: null,
                            customer_email: null,
                            customer_avatar_url: null,
                            created_at: t.created_at ?? '',
                            updated_at: t.created_at ?? '',
                            project_id: t.project_id ?? '',
                            project_name: t.project_name,
                            channel_name: null,
                            channel_type: null,
                            last_update_at: null,
                            last_update_by: null,
                            last_update_sender_name: null,
                            messages_count: 0,
                          } as TicketListItem)
                        }
                      >
                        {display}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" truncate>
                        {t.project_name || '—'}
                      </Text>
                    </Table.Td>
                    {isProduct ? (
                      <>
                        <Table.Td>
                          <Text size="sm">{t.requested_at ? formatDateTimeDot(t.requested_at) : '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c={t.review_received ? 'green' : 'dimmed'}>
                            {t.review_received
                              ? `✓ ${t.review_received_at ? formatDateTimeDot(t.review_received_at) : ''}`.trim()
                              : '— не получен'}
                          </Text>
                        </Table.Td>
                      </>
                    ) : (
                      <>
                        <Table.Td>
                          <Text size="sm">{t.status || '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{t.created_at ? formatDateTimeDot(t.created_at) : '—'}</Text>
                        </Table.Td>
                      </>
                    )}
                  </Table.Tr>
                )
              })
            )}
          </Table.Tbody>
        </Table>
        {totalPages > 1 ? (
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              Всего: {total}. Страница {page} из {totalPages}.
            </Text>
            <Group gap="xs">
              <Button
                size="xs"
                variant="default"
                disabled={page <= 1}
                onClick={() => setPage(1)}
              >
                Первая
              </Button>
              <Button
                size="xs"
                variant="default"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ←
              </Button>
              <Button
                size="xs"
                variant="default"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                →
              </Button>
              <Button
                size="xs"
                variant="default"
                disabled={page >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                Последняя
              </Button>
            </Group>
          </Group>
        ) : null}
      </Stack>
    </AppModal>
  )
}
